// src/components/resources/calculators/CreativeTestingCalculator.logic.ts

/**
 * Pure, framework-free creative-testing-budget math — extracted from
 * `CreativeTestingCalculator.tsx` so it can be unit-tested directly (A7/D4 /
 * BP1). The component imports `calculateTestingBudget` and renders; the sample-
 * size statistics, budget math, and (deterministic) recommendation logic live
 * here.
 */

export interface CalculatorInputs {
  variations: number
  goalType: 'engagement' | 'leads' | 'purchases'
  actionRate: number
  cpm: number
  detectability: number
  significance: 70 | 80 | 90
  hasCustomActionRate: boolean
}

export interface CalculatorResults {
  totalBudget: number
  budgetPerVariation: number
  requiredImpressions: number
  requiredEventsPerVariation: number
  estimatedTimeframe: string
  recommendedActions: Array<{
    type: 'primary' | 'optimization' | 'tip'
    text: string
    icon?: string
  }>
}

/**
 * Required sample size per variation via the two-proportion z-test, given a
 * base rate (%), a minimum detectable effect (proportion of base rate), and a
 * confidence level. Throws on an unmapped significance level.
 */
export function calculateRequiredSampleSize(baseRate: number, mde: number, significance: number): number {
  // Convert significance level to z-score
  const zScoreMap: Record<number, number> = {
    70: 1.04,
    80: 1.28,
    90: 1.645
  }
  const zScore = zScoreMap[significance]
  if (!zScore) {
    throw new Error(`Invalid significance level: ${significance}`)
  }

  // Statistical power (beta) - using 0.8 as standard
  const beta = 0.84

  // Calculate sample size using two-proportion z-test formula
  const p1 = baseRate / 100 // Convert to proportion
  const p2 = p1 * (1 + mde) // Effect we want to detect
  const p = (p1 + p2) / 2 // Pooled proportion

  const sampleSize = Math.ceil(
    2 * Math.pow(zScore + beta, 2) * p * (1 - p) / Math.pow(p2 - p1, 2)
  )

  return sampleSize
}

/**
 * Budget-reduction suggestions, surfaced only when the total budget is high
 * (> $10k) and the corresponding lever (goal type, confidence, detectability,
 * variation count) can be relaxed.
 */
export function getBudgetOptimizationTips(
  inputs: CalculatorInputs,
  results: CalculatorResults,
): Array<{ type: 'optimization' | 'tip'; text: string; icon: string }> {
  const tips = []

  // If budget is high, suggest ways to reduce it
  if (results.totalBudget > 10000) {
    if (inputs.goalType === 'purchases') {
      tips.push({
        type: 'optimization' as const,
        text: 'Consider starting with an engagement test (clicks, views) to identify winning concepts, then run a smaller purchase test with the best performers',
        icon: 'strategy'
      })
    }

    if (inputs.significance === 90) {
      tips.push({
        type: 'optimization' as const,
        text: 'Consider reducing confidence to 80% to lower budget requirements while maintaining reasonable reliability',
        icon: 'accuracy'
      })
    }

    if (inputs.detectability < 30) {
      tips.push({
        type: 'optimization' as const,
        text: 'Try increasing the minimum detectable effect - detecting smaller improvements requires much larger budgets',
        icon: 'sensitivity'
      })
    }

    if (inputs.variations > 4) {
      tips.push({
        type: 'optimization' as const,
        text: 'Consider testing fewer variations initially to reduce the required budget',
        icon: 'variations'
      })
    }
  }

  return tips
}

/**
 * Compute the full creative-testing budget result set: required sample size →
 * impressions → budget, plus the per-variation event target, an estimated
 * timeframe, and the combined recommended-actions list.
 */
export function calculateTestingBudget(inputs: CalculatorInputs): CalculatorResults {
  // Calculate minimum detectable effect (as a proportion of the base rate)
  const mde = inputs.detectability / 100

  // Calculate required sample size per variation
  const sampleSizePerVariation = calculateRequiredSampleSize(
    inputs.actionRate,
    mde,
    inputs.significance
  )

  // Total impressions needed across all variations
  const totalImpressions = sampleSizePerVariation * inputs.variations

  // Calculate budget
  const totalBudget = (totalImpressions / 1000) * inputs.cpm

  // Calculate required events (conversions) per variation
  const eventsPerVariation = Math.ceil(sampleSizePerVariation * (inputs.actionRate / 100))

  // Calculate budget optimization tips
  const budgetTips = getBudgetOptimizationTips(inputs, {
    totalBudget,
    budgetPerVariation: totalBudget / inputs.variations,
    requiredImpressions: totalImpressions,
    requiredEventsPerVariation: eventsPerVariation,
    estimatedTimeframe: totalImpressions > 1000000 ? '4-6 weeks' : '2-3 weeks',
    recommendedActions: []
  })

  const primaryActions = [
    {
      type: 'primary' as const,
      text: 'Run test for at least 2 weeks to account for day-of-week effects',
      icon: 'time'
    },
    {
      type: 'primary' as const,
      text: `Aim for ${eventsPerVariation} events per variation before drawing conclusions`,
      icon: 'goal'
    },
    {
      type: 'primary' as const,
      text: 'Ensure even traffic distribution across all variations',
      icon: 'distribution'
    }
  ]

  const tips = [
    {
      type: 'tip' as const,
      text: 'Monitor performance daily but avoid making early decisions',
      icon: 'monitor'
    },
    {
      type: 'tip' as const,
      text: 'Keep other variables (targeting, bidding, etc.) constant during the test',
      icon: 'control'
    }
  ]

  return {
    totalBudget,
    budgetPerVariation: totalBudget / inputs.variations,
    requiredImpressions: totalImpressions,
    requiredEventsPerVariation: eventsPerVariation,
    estimatedTimeframe: totalImpressions > 1000000 ? '4-6 weeks' : '2-3 weeks',
    recommendedActions: [...primaryActions, ...budgetTips, ...tips]
  }
}
